
<template>
  <div>
    <!-- Button to Toggle Panel -->
    <button
      v-if="isLocalhost"
      @click="$emit('toggle-panel')"
      class="fixed top-0 left-4 bg-gray-900 text-gray-300 px-3 py-1 rounded-b-lg shadow-md z-50 text-sm border-b border-l border-r border-gray-700"
      :class="isPanelVisible ? 'mt-[38px]' : 'mt-0'"
    >
      <span v-if="isPanelVisible"><i class="fas fa-caret-up mr-1"></i> ปิดหน้าต่าง</span>
      <span v-else><i class="fas fa-caret-down mr-1"></i> เปิดหน้าต่าง</span>
    </button>

    <!-- Sliding Panel -->
    <div
      class="fixed top-0 left-0 right-0 bg-gray-900 text-gray-300 shadow-lg z-40 border-b border-gray-700"
      :class="isPanelVisible ? 'translate-y-0' : '-translate-y-full'"
    >
      <div class="p-1 flex justify-between items-center">
        <span class="text-sm">
          <i class="fas fa-globe"></i> <span class="font-bold">โฮสต์ปัจจุบัน : </span>
          <span class="text-gray-400">{{ hostname }}</span>
        </span>

        <!-- Dropdown Section -->
        <div class="relative w-full max-w-lg flex justify-end">
          <!-- Dropdown Button -->
          <button
            @click="$emit('toggle-dropdown')"
            class="text-md bg-gray-800 border border-gray-600 rounded px-2 py-1 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 text-right"
          >
            <span v-if="selectedOption">
              <span class="block text-sm text-gray-400">
                <i class="fas fa-caret-down mr-1"></i> {{ selectedOption.hostname }}
              </span>
            </span>
            <span v-else><i class="fas fa-globe"></i> เลือกโฮสต์</span>
          </button>

          <!-- Remove Button -->
          <button
            @click="$emit('reset-host')"
            v-if="selectedOption"
            class="text-sm bg-red-600 border border-red-500 rounded px-2 py-1 text-white ml-2 focus:outline-none focus:ring-2 focus:ring-red-400"
          >
            <i class="fas fa-times"></i> รีเซ็ต
          </button>

          <!-- Dropdown Options with Search Bar -->
          <div
            v-if="dropdownVisible && isPanelVisible"
            class="absolute bg-gray-900 border border-gray-700 rounded mt-10 shadow-lg z-50 p-3"
            style="min-width: 300px; max-width: 600px; right: 0;"
          >
            <!-- Search Bar -->
            <div class="flex justify-between items-center mb-3">
                <input
                :value="searchQuery"
                @input="$emit('update:searchQuery', $event.target.value)"
                type="text"
                placeholder="ค้นหา..."
                class="w-full px-2 py-1 text-sm text-black rounded border border-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />              
                <!-- Close Button -->
                <button
                    @click="$emit('toggle-dropdown')"
                    class="ml-2 text-sm bg-red-600 text-white px-2 py-1 rounded hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-400 flex items-center"
                >
                    <i class="fas fa-times mr-1 text-sm"></i> ปิด
                </button>
            </div>

            <!-- Filtered Options -->
            <div v-if="props.hostsData && props.hostsData.length > 0" class="grid grid-cols-2 gap-2">
              <div
                v-for="option in filteredHosts"
                :key="option.hostname"
                :class="{
                  'px-3 py-2 bg-gray-800 hover:bg-gray-700 cursor-pointer rounded shadow-sm': option.hostname !== selectedOption?.hostname,
                  'px-3 py-2 bg-gray-600 cursor-not-allowed rounded shadow-sm': option.hostname === selectedOption?.hostname,
                }"
                @click="option.hostname !== selectedOption?.hostname && $emit('select-option', option)"
              >
                <span
                  class="block text-sm font-semibold"
                  :class="{
                    'text-gray-300': option.hostname !== selectedOption?.hostname,
                    'text-gray-500': option.hostname === selectedOption?.hostname,
                  }"
                >
                  {{ option.displayName || option.siteName }}
                </span>
                <span
                  class="block text-xs"
                  :class="{
                    'text-gray-400': option.hostname !== selectedOption?.hostname,
                    'text-gray-500': option.hostname === selectedOption?.hostname,
                  }"
                >
                  {{ option.hostname }}
                </span>
                <!-- Show additional info if available -->
                <div v-if="option.status || option.usageCount !== undefined" class="mt-1">
                  <span v-if="option.status" 
                    class="inline-block px-1 py-0.5 text-xs rounded"
                    :class="{
                      'bg-green-600 text-white': option.status === 'active' && option.hostname !== selectedOption?.hostname,
                      'bg-green-800 text-gray-300': option.status === 'active' && option.hostname === selectedOption?.hostname,
                      'bg-red-600 text-white': option.status !== 'active' && option.hostname !== selectedOption?.hostname,
                      'bg-red-800 text-gray-300': option.status !== 'active' && option.hostname === selectedOption?.hostname,
                    }"
                  >
                    {{ option.status }}
                  </span>
                  <span v-if="option.usageCount !== undefined" 
                    class="ml-1 text-xs"
                    :class="{
                      'text-gray-400': option.hostname !== selectedOption?.hostname,
                      'text-gray-500': option.hostname === selectedOption?.hostname,
                    }"
                  >
                    ({{ option.usageCount }} uses)
                  </span>
                </div>
              </div>
            </div>
            
            <!-- Loading State -->
            <div v-else-if="!props.hostsData" class="text-center py-4">
              <div class="text-gray-400">
                <i class="fas fa-spinner fa-spin mr-2"></i>
                กำลังโหลดข้อมูลโฮสต์...
              </div>
            </div>
            
            <!-- Empty State -->
            <div v-else class="text-center py-4">
              <div class="text-gray-400">
                <i class="fas fa-exclamation-triangle mr-2"></i>
                ไม่พบข้อมูลโฮสต์
              </div>
            </div>

            <!-- Total Host Count with Status Summary -->
            <div class="mt-4 text-center text-gray-300 text-sm">
              <div>โฮสต์ทั้งหมด: {{ totalHosts }}</div>
              <div v-if="filteredHosts.length !== totalHosts" class="text-gray-400">
                (แสดง: {{ filteredHosts.length }})
              </div>
              <div v-if="activeHostsCount > 0" class="text-green-400 text-xs mt-1">
                Active: {{ activeHostsCount }} | Inactive: {{ totalHosts - activeHostsCount }}
              </div>
            </div>
            
            <!-- Debug Info (remove in production) -->
            <div v-if="totalHosts === 0" class="mt-2 text-center text-xs text-gray-500">
              Debug: hostsData = {{ props.hostsData ? 'Array(' + props.hostsData.length + ')' : 'null/undefined' }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue';

// Props
const props = defineProps({
  isLocalhost: Boolean,
  isPanelVisible: Boolean,
  dropdownVisible: Boolean,
  selectedOption: Object,
  hostname: String,
  hostsData: Array,
  searchQuery: String,
});

// Debug watcher
watch(() => props.hostsData, (newValue) => {
  if (newValue) {
    //console.log('[HostPanel] hostsData received:', newValue.length, 'hosts');
  }
}, { immediate: true });

// Computed properties
const filteredHosts = computed(() => {
  if (!props.hostsData || !Array.isArray(props.hostsData)) {
    return [];
  }
  
  if (!props.searchQuery) {
    return props.hostsData.slice(-14);
  }
  
  const query = props.searchQuery.toLowerCase();
  return props.hostsData.filter((option) =>
    option.siteName?.toLowerCase().includes(query) ||
    option.hostname?.toLowerCase().includes(query) ||
    option.displayName?.toLowerCase().includes(query) ||
    option.status?.toLowerCase().includes(query)
  ).slice(-14);
});

const totalHosts = computed(() => props.hostsData?.length || 0);

const activeHostsCount = computed(() => {
  return props.hostsData?.filter(host => host.status === 'active').length || 0;
});
</script>